import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { ScoreGauge } from "@/components/ScoreGauge";
import { RiskList } from "@/components/RiskList";
import { ShareScore } from "@/components/ShareScore";
import { ShareScoreModal } from "@/components/ShareScoreModal";
import { api } from "@/lib/api";

export default function Report() {
  const { scanId } = useParams();
  const location = useLocation();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api
      .get(`/report/${scanId}`)
      .then((r) => {
        setScan(r.data);
        if (location.state?.justScanned) setShareOpen(true);
      })
      .catch(() => setScan(false))
      .finally(() => setLoading(false));
  }, [scanId, location.state]);

  const exportReport = () => {
    if (!scan) return;
    const token = localStorage.getItem("warden_token");
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/scans/${scanId}/report.pdf${token ? `?auth=${token}` : ""}`;
    window.open(url, "_blank");
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030812] text-slate-400">
        <Loader2 className="animate-spin mr-2" /> Loading report…
      </div>
    );
  if (!scan)
    return (
      <div className="min-h-screen text-white">
        <Navbar />
        <div className="pt-32 text-center text-slate-400">Report not found.</div>
      </div>
    );

  const c = scan.severity_counts || {};
  return (
    <div className="min-h-screen text-white">
      <Navbar />
      <main className="pt-24 pb-20 max-w-5xl mx-auto px-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
            <ArrowLeft size={16} /> Back to dashboard
          </Link>
          <button
            onClick={exportReport}
            data-testid="export-pdf-button"
            className="flex items-center gap-2 border border-slate-700 hover:border-cyanbrand text-white px-4 py-2 rounded-md text-sm transition-colors"
          >
            <Download size={16} /> Export PDF
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 items-center bg-[#0C1424] border border-slate-800 rounded-lg p-8 mt-6">
          <div className="flex justify-center">
            <ScoreGauge score={scan.score} grade={scan.grade} />
          </div>
          <div className="lg:col-span-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyanbrand">Full security report</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold mt-2 font-mono break-all">{scan.host}</h1>
            <p className="text-slate-300 mt-4 leading-relaxed" data-testid="report-summary">{scan.ai_summary}</p>
            <div className="flex flex-wrap gap-3 mt-5">
              {["critical", "high", "medium", "low"].map((sev) => (
                <span key={sev} className="text-sm px-3 py-1 rounded-full border border-slate-700 text-slate-300">
                  <b className="text-white">{c[sev] || 0}</b> {sev}
                </span>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-4">Scanned {new Date(scan.scanned_at).toLocaleString()}</p>
            <ShareScore scanId={scan.scan_id} host={scan.host} score={scan.score} />
          </div>
        </div>

        <h2 className="font-heading text-xl font-semibold mt-8 mb-4">All findings</h2>
        <RiskList findings={scan.findings} explanations={scan.ai_explanations} />
      </main>

      <ShareScoreModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        scanId={scan.scan_id}
        host={scan.host}
        score={scan.score}
      />
    </div>
  );
}
